import { createContext, useContext } from "react";

export type ThreadDocument = {
  docId: string;
  name: string | undefined;
  caseId?: string | null;
  /** File extension token (lower-case, no dot) — e.g. "pdf", "docx". From backend's
   *  resolved Praetor lookup or filename-extension fallback. Null when unknown. */
  type?: string | null;
};

export type ThreadItem = {
  remoteId: string;
  selectedDocs: ThreadDocument[];
  isPinned?: boolean;
  /** ISO timestamp of thread creation. Populated by the list endpoint. */
  createdAt?: string;
  /** ISO timestamp of last update. Drives chats-page time-bucket grouping. */
  updatedAt?: string;
  /** ISO timestamp of archive; null/undefined means the thread is active. */
  archivedAt?: string | null;
  /** Plain title duplicated here so the chats page can read it without a separate AUI subscription. */
  title?: string;
  /** Owning case id (null/undefined means the default workspace). Lets the chats page link
   *  directly to /{caseId}/{threadId} instead of guessing "default" for workspace-level rows. */
  caseId?: string | null;
  /** Primary case anchor (GUID). Null = workspace-level thread (no case context). */
  mainCaseId?: string | null;
  /** Additional case IDs approved via elicitation or scope endpoint. */
  additionalCaseIds?: string[] | null;
  /** Per-case document restrictions. Key = caseId, value = approved docId list. */
  additionalDocumentsByCase?: Record<string, string[]> | null;
};

// --- Thread List Context (threadItems + isListLoading) ---

export type ThreadListData = {
  isListLoading: boolean;
  threadItems: ThreadItem[] | [];
};

export type ThreadListContextValues = ThreadListData & {
  onThreadListChanged: (update: Partial<ThreadListData>) => void;
};

export const ThreadListContext = createContext<ThreadListContextValues | undefined>(undefined);

export function useThreadListContext() {
  const ctx = useContext(ThreadListContext);
  if (!ctx) throw new Error("useThreadListContext must be used under <ThreadListContext.Provider>");
  return ctx;
}

// --- Thread Docs Context (selectedDocs) ---

export type ThreadDocsData = {
  selectedDocs: ThreadDocument[] | null;
};

export type ThreadDocsContextValues = ThreadDocsData & {
  onThreadDocsChanged: (update: Partial<ThreadDocsData>) => void;
};

export const ThreadDocsContext = createContext<ThreadDocsContextValues | undefined>(undefined);

export function useThreadDocsContext() {
  const ctx = useContext(ThreadDocsContext);
  if (!ctx) throw new Error("useThreadDocsContext must be used under <ThreadDocsContext.Provider>");
  return ctx;
}

// --- Legacy combined type (for backward compat in customThreadListProvider ref) ---

export type ThreadData = {
  isListLoading: boolean;
  selectedDocs: ThreadDocument[] | null;
  threadItems: ThreadItem[] | [];
};

export type ThreadContextValues = ThreadData & {
  onThreadStateChanged: (update: Partial<ThreadData>) => void;
};

export const ThreadContext = createContext<ThreadContextValues | undefined>(undefined);

export function useThreadContext() {
  const ctx = useContext(ThreadContext);
  
  if (!ctx) throw new Error("useThreadContext must be used under <ThreadContext.Provider>");
  return ctx;
}